import { useEffect, useRef, useState, type FC } from 'react';
import './BrikAgente.css';

/** `falando` (07/10/2026): a boca abre e fecha enquanto ele fala — no grande. */
export type EstadoBrik = 'repouso' | 'chamado' | 'pensando' | 'feito' | 'falando';

/* A boca da direção D (docs/designsystem/propostas/brik-boca.html, 07/10/2026),
   na grade de 112: centrada em x 49, logo abaixo dos olhos (que vão até y 59,5). */
const BOCA_REPOUSO = 'M44 64.4 Q49 68.6 54 64.4';
const BOCA_SORRISO = 'M42.5 63.8 Q49 69.8 55.5 63.8';
/* Meia-lua de boca aberta: topo reto em y 63,4, `l` de meia largura e `h` de fundo. */
const meiaLua = (l: number, h: number) =>
    `M${49 - l} 63.4 H${49 + l} Q${49 + l} ${63.4 + h} 49 ${63.4 + h} Q${49 - l} ${63.4 + h} ${49 - l} 63.4 Z`;

/**
 * A cara do Brik (07/10/2026, desenho da Home: `docs/designsystem/propostas/home-brik.html`,
 * seção 6). Só olhos e corpo, no mesmo tijolo da marca. `atento` é o de sempre;
 * as outras três existem na versão grande, e a pequena ganha só `feliz` (no pé
 * da resposta, quando uma ação termina). Nenhuma cara lê como brava ou triste.
 * Na Home ele não fecha o olho: *"tinha que estar aberto e piscando, movendo os
 * olhos"* (07/10) — por isso o comemorando também é de olho aberto, e o `feliz`
 * de arcos ficou só no Brik pequeno.
 */
export type CaraBrik = 'atento' | 'feliz' | 'comemorando' | 'dormindo';

const reduzMovimento = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

/* Para onde as pupilas do Brik grande olham no modo `vivo`, como deslocamento
   desde o lugar de sempre (38,5 × 50, levemente à direita e acima do centro do
   olho, que é 37 × 51). Todas cabem dentro do olho (7 × 8,5, pupila de 3,4).
   A primeira é o balão, à direita: é para onde ele olha quando a fala chega. */
const OLHARES: Array<[number, number]> = [
    [1.3, 0.5], // o balão, à direita
    [-1.5, 1], // de frente
    [-4.3, 1], // à esquerda
    [0.7, -2.2], // em cima, à direita
    [-3.7, -2.2], // em cima, à esquerda
    [0.7, 4], // embaixo, à direita: o campo
    [-3.7, 4], // embaixo, à esquerda
];

/**
 * O Brik — o agente que age em toda tela, um tijolinho com olhos.
 *
 * Marca fechada em `docs/propostas/marca-do-agente.html`: a versão pequena é
 * redesenhada na grade de 24 (faces grossas, olhos grandes, para não virar
 * mancha); a grande tem mais detalhe. Cores só por `--mrc-*`.
 *
 * Em repouso ele pisca sozinho num intervalo sorteado de 20 a 30 s (sorteado
 * para não parecer relógio) e, a cada três piscadas, olha rápido para o lado.
 * Para com a aba escondida, com `animar` falso (painel aberto) e com
 * `prefers-reduced-motion`.
 */
const BrikAgente: FC<{
    tamanho?: 'pequeno' | 'grande';
    /** Lado em px. Pequeno: 24 no botão, 20 no topo do painel, 26 no celular. Grande: 96 de altura. */
    px?: number;
    estado?: EstadoBrik;
    /** Falso desliga o piscar de repouso (ex.: com o painel aberto, o botão fica por baixo). */
    animar?: boolean;
    /** A expressão. Fora `atento`, a cara não pisca nem espia: ela já diz o que sente. */
    cara?: CaraBrik;
    /**
     * Pequeno, recortado no tijolo: `px` vira a ALTURA do tijolo, e não a da caixa
     * de 24 (onde ele ocupa 22×15 e o resto é ar). É o do cabeçalho, que pede o
     * tijolo da altura dos botões vizinhos (07/10/2026).
     */
    justo?: boolean;
    /**
     * Grande, de olho aberto e em repouso: a cada 1,6–3,6 s ele olha para outro
     * lugar, pisca ou pisca duas vezes. É o Brik da Home (pedido dele em 07/10:
     * "aberto e piscando, movendo os olhos, alternando"). Sem ele, o repouso é o
     * de sempre: uma piscada a cada 20–30 s.
     */
    vivo?: boolean;
    /**
     * Com `vivo`: as pupilas seguem o ponteiro do mouse pela tela, e ele continua
     * piscando. Com o mouse parado por 3 s, ou fora da janela, ele volta a olhar
     * em volta sozinho. É o da tela inicial da Home (pedido dele em 07/10/2026).
     */
    segueMouse?: boolean;
}> = ({ tamanho = 'pequeno', px, estado = 'repouso', animar = true, cara = 'atento', justo = false, vivo = false, segueMouse = false }) => {
    const [gesto, setGesto] = useState<string>('');
    const piscadas = useRef(0);
    const refSvg = useRef<SVGSVGElement>(null);
    /* Quando o mouse mexeu pela última vez (0 = longe ou fora da janela). */
    const ultimoMouse = useRef(0);

    const olhoAberto = cara === 'atento' || cara === 'comemorando';
    const vivoLigado = vivo && tamanho === 'grande' && estado === 'repouso' && animar && olhoAberto;

    /* Repouso: um gesto de cada vez, agendado com intervalo sorteado. Só a cara
       de olhos abertos pisca — olho fechado de feliz ou de sono não "pisca". O
       modo vivo tem o seu próprio ritmo, logo abaixo. */
    useEffect(() => {
        if (estado !== 'repouso' || !animar || cara !== 'atento' || vivo || reduzMovimento()) return;

        let montado = true;
        let espera: ReturnType<typeof setTimeout>;
        let limpa: ReturnType<typeof setTimeout>;

        const agendar = () => {
            espera = setTimeout(() => {
                if (!montado) return;
                if (!document.hidden) {
                    piscadas.current += 1;
                    const espiar = piscadas.current % 3 === 0;
                    setGesto(espiar ? 'is-pisca is-espia' : 'is-pisca');
                    limpa = setTimeout(() => { if (montado) setGesto(''); }, espiar ? 1500 : 320);
                }
                agendar();
            }, 20000 + Math.random() * 10000);
        };

        agendar();
        return () => { montado = false; clearTimeout(espera); clearTimeout(limpa); setGesto(''); };
    }, [estado, animar, cara, vivo]);

    /* Vivo: começa olhando para o balão e, a cada 1,6–3,6 s, faz uma coisa só —
       olha para outro lugar (perto da metade das vezes), pisca, pisca duas vezes,
       ou, de vez em quando (1 em 8), sorri: os olhos viram os arcos de feliz por
       um instante, com um pulinho (pedido dele em 07/10: "piscar desse jeito em
       alguns momentos e dar um pulinho"). Seguindo o mouse, o olhar é do mouse e
       aqui ele só pisca ou sorri. Para com a aba escondida e com movimento
       reduzido. */
    const [olhar, setOlhar] = useState<[number, number] | null>(null);
    const [sorriso, setSorriso] = useState(false);
    useEffect(() => {
        if (!vivoLigado || reduzMovimento()) { setOlhar(null); setSorriso(false); return; }

        let ligado = true;
        let espera: ReturnType<typeof setTimeout>;
        let limpa: ReturnType<typeof setTimeout>;
        let atual = 0;
        setOlhar(OLHARES[0]);

        const agendar = () => {
            espera = setTimeout(() => {
                if (!ligado) return;
                if (!document.hidden) {
                    const sorte = Math.random();
                    const piscar = (duas: boolean) => {
                        setGesto(duas ? 'is-pisca2' : 'is-pisca');
                        limpa = setTimeout(() => { if (ligado) setGesto(''); }, duas ? 950 : 320);
                    };
                    const sorrir = () => {
                        setSorriso(true);
                        setGesto('is-sorri');
                        limpa = setTimeout(() => { if (ligado) { setSorriso(false); setGesto(''); } }, 900);
                    };
                    if (sorte < 0.12) {
                        sorrir();
                    } else if (Date.now() - ultimoMouse.current < 3000) {
                        if (sorte < 0.45) piscar(sorte >= 0.38);
                    } else if (sorte < 0.6) {
                        let i = atual;
                        while (i === atual) i = Math.floor(Math.random() * OLHARES.length);
                        atual = i;
                        setOlhar(OLHARES[i]);
                    } else {
                        piscar(sorte >= 0.88);
                    }
                }
                agendar();
            }, 1600 + Math.random() * 2000);
        };

        agendar();
        return () => { ligado = false; clearTimeout(espera); clearTimeout(limpa); setGesto(''); setSorriso(false); };
    }, [vivoLigado]);

    /* Seguindo o mouse: a cada movimento (um cálculo por quadro), as pupilas
       apontam para o ponteiro, a partir do meio dos dois olhos. Perto do Brik
       elas andam pouco; a partir de ~260px, vão até a beira do olho. */
    useEffect(() => {
        if (!vivoLigado || !segueMouse || reduzMovimento()) return;

        let quadro = 0;
        let ponto: { x: number; y: number } | null = null;

        const mirar = () => {
            quadro = 0;
            const svg = refSvg.current;
            if (!svg || !ponto) return;
            const r = svg.getBoundingClientRect();
            if (!r.width) return;
            /* O meio dos dois olhos: 49 × 51 na grade de 112 × 84. */
            const vx = ponto.x - (r.left + (49 / 112) * r.width);
            const vy = ponto.y - (r.top + (51 / 84) * r.height);
            const distancia = Math.hypot(vx, vy);
            if (distancia < 1) return;
            const forca = Math.min(1, distancia / 260);
            /* Até onde a pupila anda a partir do centro do olho (cabe no olho de
               7 × 8,5 com a pupila de 3,4), menos o lugar de sempre dela, que já
               é 1,5 à direita e 1 acima do centro. */
            const alvo: [number, number] = [
                Math.round(((vx / distancia) * 3 * forca - 1.5) * 10) / 10,
                Math.round(((vy / distancia) * 4 * forca + 1) * 10) / 10,
            ];
            setOlhar(o => (o && o[0] === alvo[0] && o[1] === alvo[1] ? o : alvo));
        };

        const aoMover = (e: MouseEvent) => {
            ponto = { x: e.clientX, y: e.clientY };
            ultimoMouse.current = Date.now();
            if (!quadro) quadro = requestAnimationFrame(mirar);
        };
        /* O ponteiro saiu da janela: ele volta a olhar em volta na próxima vez. */
        const aoSair = () => { ultimoMouse.current = 0; };

        window.addEventListener('mousemove', aoMover, { passive: true });
        document.documentElement.addEventListener('mouseleave', aoSair);
        return () => {
            window.removeEventListener('mousemove', aoMover);
            document.documentElement.removeEventListener('mouseleave', aoSair);
            if (quadro) cancelAnimationFrame(quadro);
            ultimoMouse.current = 0;
        };
    }, [vivoLigado, segueMouse]);

    /* Feito e chamado são gestos de uma vez: a classe entra e sai sozinha. */
    const [unico, setUnico] = useState('');
    useEffect(() => {
        if (estado !== 'feito' && estado !== 'chamado') { setUnico(''); return; }
        if (reduzMovimento()) { setUnico(estado === 'chamado' ? 'is-chamado' : ''); return; }
        setUnico(`is-${estado}`);
        if (estado === 'feito') {
            const t = setTimeout(() => setUnico(''), 900);
            return () => clearTimeout(t);
        }
    }, [estado]);

    /* Comemorar também é de uma vez: o pulo e o confete quando a cara chega. */
    const [festa, setFesta] = useState(false);
    useEffect(() => {
        if (cara !== 'comemorando' || reduzMovimento()) { setFesta(false); return; }
        setFesta(true);
        const t = setTimeout(() => setFesta(false), 1300);
        return () => clearTimeout(t);
    }, [cara]);

    const classes = [
        'mrc',
        tamanho === 'grande' ? 'is-grande' : '',
        estado === 'pensando' ? 'is-pensando' : '',
        estado === 'falando' ? 'is-falando' : '',
        `cara-${cara}`,
        festa ? 'is-comemora' : '',
        vivoLigado ? 'is-vivo' : '',
        unico,
        gesto,
    ].filter(Boolean).join(' ');

    if (tamanho === 'grande') {
        const alto = px ?? 96;
        const comemora = cara === 'comemorando';

        /* Comemorando é de olho aberto (07/10): a festa é o pulo e o confete. O
           sorriso do vivo usa os arcos de feliz por um instante. */
        const olhos = cara === 'feliz' || (sorriso && olhoAberto) ? (
            <g className="mrc-olhos">
                <path className="mrc-traco" d="M30 54.5 Q37 44 44 54.5" />
                <path className="mrc-traco" d="M54 54.5 Q61 44 68 54.5" />
            </g>
        ) : cara === 'dormindo' ? (
            <g className="mrc-olhos">
                <path className="mrc-traco" d="M30.5 50.5 Q37 57 43.5 50.5" />
                <path className="mrc-traco" d="M54.5 50.5 Q61 57 67.5 50.5" />
            </g>
        ) : (
            <g className="mrc-olhos">
                <ellipse cx="37" cy="51" rx="7" ry="8.5" fill="var(--mrc-olho)" />
                <ellipse cx="61" cy="51" rx="7" ry="8.5" fill="var(--mrc-olho)" />
                <g
                    className="mrc-pupilas"
                    style={olhar ? { transform: `translate(${olhar[0]}px, ${olhar[1]}px)` } : undefined}
                >
                    <circle cx="38.5" cy="50" r="3.4" fill="var(--mrc-pupila)" />
                    <circle cx="62.5" cy="50" r="3.4" fill="var(--mrc-pupila)" />
                </g>
            </g>
        );

        /* A boca (direção D, com boca também em repouso — pedido dele em 07/10):
           sorriso discreto parado, boquinha que abre e fecha falando, sorriso
           largo no "^ ^", boca aberta comemorando. Pensando (as bolhas) e
           dormindo, sem boca. Na cor da pupila, que acompanha o tema. */
        let boca = null;
        if (comemora) {
            boca = <path className="mrc-boca" d={meiaLua(5.6, 5.4)} fill="var(--mrc-pupila)" />;
        } else if (estado === 'falando' && olhoAberto) {
            boca = <path className="mrc-boca" d={meiaLua(3.6, 3.8)} fill="var(--mrc-pupila)" />;
        } else if (cara !== 'dormindo' && estado !== 'pensando') {
            const sorrindo = cara === 'feliz' || (sorriso && olhoAberto);
            boca = (
                <path
                    className="mrc-boca"
                    d={sorrindo ? BOCA_SORRISO : BOCA_REPOUSO}
                    fill="none"
                    stroke="var(--mrc-pupila)"
                    strokeWidth={2.4}
                    strokeLinecap="round"
                />
            );
        }

        const corpo = (
            <g className="mrc-corpo">
                <path d="M10 30 L24 16 H102 L88 30 Z" fill="var(--mrc-a2)" />
                <path d="M88 30 L102 16 V60 L88 74 Z" fill="var(--mrc-lado)" />
                <rect x="10" y="30" width="78" height="44" rx="3" fill="var(--mrc-a)" />
                {olhos}
                {boca}
            </g>
        );

        return (
            <svg ref={refSvg} className={classes} viewBox="0 0 112 84" width={(alto * 112) / 84} height={alto} aria-hidden="true">
                {/* Comemorando, ele fica no ar: o corpo sobe e a sombra encolhe. */}
                {comemora ? <g transform="translate(0 -6)">{corpo}</g> : corpo}
                <ellipse cx="54" cy="81" rx={comemora ? 30 : 40} ry={comemora ? 1.8 : 2} fill="var(--mrc-borda)" />
                {comemora && (
                    /* O confete são tijolinhos, nas cores do Brik e no ciano da marca. */
                    <g className="mrc-confete">
                        <rect x="0" y="12" width="8" height="4.5" rx="1" fill="var(--mrc-a2)" transform="rotate(-24 4 14)" />
                        <rect x="17" y="1" width="7" height="4" rx="1" fill="var(--mrc-ciano)" transform="rotate(18 20 3)" />
                        <rect x="58" y="-5" width="6" height="3.5" rx="1" fill="var(--mrc-lado)" transform="rotate(-30 61 -3)" />
                        <rect x="97" y="1" width="8" height="4.5" rx="1" fill="var(--mrc-ciano)" transform="rotate(28 101 3)" />
                        <rect x="105" y="20" width="6" height="3.5" rx="1" fill="var(--mrc-a2)" transform="rotate(-15 108 22)" />
                    </g>
                )}
                {cara === 'dormindo' && (
                    <g className="mrc-zz" fill="var(--mrc-a2)" fontFamily="Inter, sans-serif" fontWeight={700}>
                        <text x="94" y="13" fontSize="17">z</text>
                        <text x="106" y="-1" fontSize="12">z</text>
                    </g>
                )}
                {/* Pensando (P2A, 07/10): três bolhas saem do canto do tijolo e
                    acendem uma de cada vez; os olhos sobem para elas (CSS). */}
                {estado === 'pensando' && (
                    <g>
                        <circle className="mrc-bolha b1" cx="97" cy="12" r="1.8" fill="var(--mrc-a2)" />
                        <circle className="mrc-bolha b2" cx="103.5" cy="4" r="2.4" fill="var(--mrc-a2)" />
                        <circle className="mrc-bolha b3" cx="111" cy="-5" r="3.1" fill="var(--mrc-a2)" />
                    </g>
                )}
            </svg>
        );
    }

    /* O pequeno só tem boca quando o tijolo tem altura para ela: o do cabeçalho
       (32px, recortado). A 24px ela viraria um tracinho de 2px, uma mancha. */
    const bocaPequena = justo && (px ?? 24) >= 28 && cara !== 'feliz';

    const lado = px ?? 24;
    /* Justo: a caixa é o próprio tijolo (x 1–23, y 4,5–19,5 da grade de 24). O
       pulo e o olhar saem dela sem corte, porque o .mrc tem overflow visível. */
    return (
        <svg
            className={classes}
            viewBox={justo ? '1 4.5 22 15' : '0 0 24 24'}
            width={justo ? (lado * 22) / 15 : lado}
            height={lado}
            aria-hidden="true"
        >
            <g className="mrc-corpo">
                <path d="M1 7 L3.5 4.5 H23 L20.5 7 Z" fill="var(--mrc-a2)" />
                <path d="M20.5 7 L23 4.5 V17 L20.5 19.5 Z" fill="var(--mrc-lado)" />
                <rect x="1" y="7" width="19.5" height="12.5" rx="1.4" fill="var(--mrc-a)" />
                {cara === 'feliz' ? (
                    <g className="mrc-olhos">
                        <path className="mrc-traco" d="M5.3 14.4 Q7.6 10.9 9.9 14.4" />
                        <path className="mrc-traco" d="M11.6 14.4 Q13.9 10.9 16.2 14.4" />
                    </g>
                ) : (
                    <g className="mrc-olhos">
                        <ellipse cx="7.6" cy="13.3" rx="2.5" ry="3" fill="var(--mrc-olho)" />
                        <ellipse cx="13.9" cy="13.3" rx="2.5" ry="3" fill="var(--mrc-olho)" />
                        <g className="mrc-pupilas">
                            <circle cx="8.1" cy="13.1" r="1.3" fill="var(--mrc-pupila)" />
                            <circle cx="14.4" cy="13.1" r="1.3" fill="var(--mrc-pupila)" />
                        </g>
                    </g>
                )}
                {bocaPequena && (
                    <path
                        className="mrc-boca"
                        d="M9.5 17.6 Q10.75 18.7 12 17.6"
                        fill="none"
                        stroke="var(--mrc-pupila)"
                        strokeWidth={0.9}
                        strokeLinecap="round"
                    />
                )}
            </g>
        </svg>
    );
};

export default BrikAgente;
